import { useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from 'redux/hooks';
import { Form, useFormikContext } from 'formik';
import styles from './ablineForm.module.scss';
import ABLineCreationOptions from './subcomponents/ABLineCreationOptions';
import ABLineAdjustmentOptions from './subcomponents/ABLineAdjustmentOptions';
import ShapefileVisualizer from '../../global/ShapefileVisualizer/ShapefileVisualizer';

interface ABLineFormProps {
  imageUrl: string;
  isLoading: boolean;
  task: string;
  generatePreview: (values?: Record<string, any>) => void;
  error: string | null;
}

const ABLineForm = ({
  imageUrl,
  isLoading,
  task,
  generatePreview,
  error,
}: ABLineFormProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { trial } = useAppSelector((state) => state.createTrial);
  const isInitialMount = useRef(true);
  const previousMethod = useRef(values.get_abline_method);
  const previousGeojson = useRef(values.abline_geojson);

  const isHeadingError = error?.includes('heading');

  // Only run on initial mount to generate the first preview
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (error === null && values.get_abline_method === 'guess') {
        generatePreview(values);
      }
    }
  }, [error, generatePreview, values]);

  // Generate preview when method changes
  useEffect(() => {
    if (
      !isInitialMount.current &&
      previousMethod.current !== values.get_abline_method
    ) {
      previousMethod.current = values.get_abline_method;
      generatePreview(values);
    }
  }, [values.get_abline_method, generatePreview, values]);

  // Handle file uploads separately
  useEffect(() => {
    // Only trigger if geojson has changed but method hasn't
    if (
      !isInitialMount.current &&
      values.get_abline_method === 'upload' &&
      values.abline_geojson !== null &&
      previousGeojson.current !== values.abline_geojson
    ) {
      previousGeojson.current = values.abline_geojson;
      generatePreview(values);
    }
  }, [values.abline_geojson, generatePreview, values]);

  const generatePreviewIfValid = useCallback(
    (name: string, value?: string) => {
      const newValues = { ...values };

      if (value !== undefined) {
        setFieldValue(name, value);
        newValues[name] = value;
      }

      generatePreview(newValues);
    },
    [values, setFieldValue, generatePreview],
  );

  // Get the appropriate AB line coordinates based on task
  const getABLineCoordinates = () => {
    if (task === 'creation') {
      // If we have coordinates directly in the state, use those
      if (trial.abline_coordinates) {
        return trial.abline_coordinates;
      }
      // Otherwise, if we have GeoJSON, extract the coordinates from it
      else if (
        trial.abline_geojson &&
        trial.abline_geojson.features &&
        trial.abline_geojson.features.length > 0 &&
        trial.abline_geojson.features[0].geometry
      ) {
        return trial.abline_geojson.features[0].geometry.coordinates;
      }
      return undefined;
    } else {
      // For adjustment, use adjusted coordinates if available, otherwise use original
      if (trial.abline_adjusted_coordinates) {
        return trial.abline_adjusted_coordinates;
      } else if (trial.abline_coordinates) {
        return trial.abline_coordinates;
      }
      // Otherwise, if we have GeoJSON, extract the coordinates from it
      else if (
        trial.abline_geojson &&
        trial.abline_geojson.features &&
        trial.abline_geojson.features.length > 0 &&
        trial.abline_geojson.features[0].geometry
      ) {
        return trial.abline_geojson.features[0].geometry.coordinates;
      }
      return undefined;
    }
  };

  return (
    <Form className={styles['abline-form']}>
      {task === 'creation' && (
        <ABLineCreationOptions
          generatePreviewIfValid={generatePreviewIfValid}
          generatePreview={generatePreview}
          trial={trial}
        />
      )}
      {task === 'adjustment' && (
        <ABLineAdjustmentOptions
          generatePreviewIfValid={generatePreviewIfValid}
        />
      )}

      {/* Use ShapefileVisualizer for both creation and adjustment */}
      <div className={styles['visualizer-container']}>
        {isLoading ? (
          <div className={styles['loading-container']}>Loading AB line...</div>
        ) : isHeadingError ? (
          // For heading required error, show both visualizer and overlay
          <>
            <ShapefileVisualizer
              fieldBoundaryGeoJSON={trial.field_boundary_geojson}
              trialBoundaryGeoJSON={
                trial.trial_boundary_is_field_boundary
                  ? undefined
                  : trial.trial_boundary_geojson
              }
              abLineCoordinates={undefined} // Don't show AB line when heading is missing
              height="400px"
              width="100%"
            />
            <div className={styles['heading-error-overlay']}>{error}</div>
          </>
        ) : error ? (
          // For other errors, keep existing behavior
          <div className={styles['error-container']}>{error}</div>
        ) : (
          <ShapefileVisualizer
            fieldBoundaryGeoJSON={trial.field_boundary_geojson}
            trialBoundaryGeoJSON={
              trial.trial_boundary_is_field_boundary
                ? undefined
                : trial.trial_boundary_geojson
            }
            abLineCoordinates={getABLineCoordinates()}
            height="400px"
            width="100%"
          />
        )}
      </div>
    </Form>
  );
};

export default ABLineForm;
